<template>
  <div v-if="columns.length > 0" class="ticket-stage-timeline">
    <div
      v-for="(col, idx) in columns"
      :key="col.id"
      class="stage-block"
      :class="stageClass(idx)"
      :style="stageInlineStyle(idx)"
    >
      <span class="stage-name">{{ col.name }}</span>
      <span v-if="stageDays(col.id, idx) !== null" class="stage-days">
        {{ stageDays(col.id, idx) }}d
      </span>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import ticketLogsApi from 'dashboard/api/ticketLogs';

export default {
  name: 'TicketStageTimeline',
  props: {
    ticket: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      stageLogs: [],
      loading: false,
    };
  },
  computed: {
    ...mapGetters({
      kanbanList: 'getKanbanfunnels',
    }),
    kanban() {
      if (!this.ticket?.kanbanfunnel_id) return null;
      const fid = String(this.ticket.kanbanfunnel_id);
      return this.kanbanList.find(k => String(k.id) === fid) || null;
    },
    columns() {
      if (!this.kanban?.columns) return [];
      return [...this.kanban.columns].sort((a, b) => (a.index || 0) - (b.index || 0));
    },
    currentColumnIndex() {
      if (!this.ticket?.columns_id) return -1;
      const cid = String(this.ticket.columns_id);
      return this.columns.findIndex(c => String(c.id) === cid);
    },
    /**
     * Build a map: columnId -> days spent
     * Uses status_changed logs ordered by date to compute intervals.
     * The ticket started in its initial column at created_at.
     */
    daysMap() {
      const map = {};
      if (!this.ticket?.id || !this.columns.length) return map;

      const now = Date.now();
      // created_at comes as ISO string from the API
      const ticketCreatedAt = this.ticket.created_at
        ? new Date(this.ticket.created_at).getTime()
        : now;

      // Filter only column-move logs (status_changed), sorted oldest→newest
      const moves = [...this.stageLogs]
        .filter(l => l.event_type === 'status_changed')
        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

      if (moves.length === 0) {
        // Never moved — all time in current column
        const currentColId = this.ticket.columns_id;
        if (currentColId) {
          map[currentColId] =
            (map[currentColId] || 0) +
            Math.floor((now - ticketCreatedAt) / 86400000);
        }
        return map;
      }

      // Build a sequential list of [columnId, enterTime] pairs
      // Entry 0: ticket was created in old_status of the first move
      const intervals = [];

      const firstMove = moves[0];
      const firstColId = firstMove.old_status?.id;
      if (firstColId) {
        intervals.push({ colId: firstColId, enterAt: ticketCreatedAt });
      }

      for (const move of moves) {
        const exitAt = new Date(move.created_at).getTime();
        const enterColId = move.new_status?.id;

        // Close the last open interval
        if (intervals.length > 0) {
          const last = intervals[intervals.length - 1];
          if (!last.exitAt) last.exitAt = exitAt;
        }

        // Open a new interval for the destination column
        if (enterColId) {
          intervals.push({ colId: enterColId, enterAt: exitAt });
        }
      }

      // Close the last interval with "now"
      if (intervals.length > 0) {
        const last = intervals[intervals.length - 1];
        if (!last.exitAt) last.exitAt = now;
      }

      // Accumulate days per column (handles revisits)
      for (const { colId, enterAt, exitAt } of intervals) {
        const days = Math.floor((exitAt - enterAt) / 86400000);
        map[colId] = (map[colId] || 0) + days;
      }

      return map;
    },
  },
  watch: {
    'ticket.id': {
      immediate: true,
      handler(id) {
        if (id) this.fetchLogs();
      },
    },
  },
  created() {
    this.$store.dispatch('fetchKanbanfunnels');
  },
  methods: {
    async fetchLogs() {
      if (!this.ticket?.id) return;
      this.loading = true;
      try {
        const resp = await ticketLogsApi.getLogs({
          ticketId: this.ticket.id,
          perPage: 200,
        });
        this.stageLogs = resp?.data?.ticket_logs || [];
      } catch (_e) {
        this.stageLogs = [];
      } finally {
        this.loading = false;
      }
    },

    stageClass(idx) {
      const cur = this.currentColumnIndex;
      if (cur >= 0) {
        if (idx < cur) return 'stage--past';
        if (idx === cur) return 'stage--current';
        return 'stage--future';
      }
      const colId = this.columns[idx]?.id;
      if (colId && this.daysMap[colId] !== undefined) return 'stage--past';
      return 'stage--future';
    },

    stageInlineStyle(idx) {
      const cls = this.stageClass(idx);
      if (cls === 'stage--past') return { background: 'rgba(40,167,69,0.65)' };
      if (cls === 'stage--current') return { background: '#28a745' };
      return { background: '#e5e7eb' };
    },

    stageDays(colId, idx) {
      const cur = this.currentColumnIndex;
      if (cur >= 0 && idx > cur) return null;
      const days = this.daysMap[colId];
      if (days === undefined) return null;
      return days;
    },
  },
};
</script>

<style lang="scss" scoped>
// Pipeline block height
$block-height: 36px;
$arrow-size: 10px;

.ticket-stage-timeline {
  display: flex;
  align-items: center;
  width: 100%;
  overflow-x: auto;
  padding: 8px 16px;
  background: var(--white);
  border-bottom: 1px solid var(--s-75);
  gap: 2px;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.stage-block {
  position: relative;
  flex: 1;
  min-width: 72px;
  height: $block-height;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  padding: 0 8px 0 14px;
  cursor: default;
  clip-path: polygon(
    0% 0%,
    calc(100% - #{$arrow-size}) 0%,
    100% 50%,
    calc(100% - #{$arrow-size}) 100%,
    0% 100%,
    #{$arrow-size} 50%
  );

  // First block: no left indent
  &:first-child {
    clip-path: polygon(
      0% 0%,
      calc(100% - #{$arrow-size}) 0%,
      100% 50%,
      calc(100% - #{$arrow-size}) 100%,
      0% 100%
    );
    padding-left: 10px;
  }

  // Last block: no right arrow
  &:last-child {
    clip-path: polygon(
      0% 0%,
      100% 0%,
      100% 100%,
      0% 100%,
      #{$arrow-size} 50%
    );
  }

  // Single block (first AND last)
  &:first-child:last-child {
    clip-path: none;
    border-radius: 4px;
    padding-left: 10px;
  }

  .stage-name {
    font-size: 11px;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    line-height: 1.2;
  }

  .stage-days {
    font-size: 10px;
    line-height: 1.3;
    opacity: 0.8;
  }

  /* ── Past stages ── */
  &.stage--past {
    background: rgba(40, 167, 69, 0.65);

    .stage-name { color: #fff; }
    .stage-days { color: rgba(255,255,255,0.85); }
  }

  /* ── Current stage ── */
  &.stage--current {
    background: #28a745;

    .stage-name {
      color: #fff;
      font-weight: 600;
    }
    .stage-days { color: rgba(255,255,255,0.9); }
  }

  /* ── Future stages ── */
  &.stage--future {
    background: var(--s-75);

    .stage-name { color: var(--s-400); }
    .stage-days { color: transparent; }
  }
}
</style>
